import { origemAtual } from "@/lib/navegacao";
import { useState } from "react";
import { Link } from "wouter";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/data/status-badge";
import { Plus, Trash2, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useExcluirAdicional, type LinhaMensalistaMes, type QuinzenaDaCompetencia, type FechamentoDoMes } from "@/hooks/use-equipe";
import {
  formatarBRL, formatarDataCurta, paraReais, normalizarDatasISO,
  estadoLinhaMes, situacaoAdiantamentoMes, salarioLinhaMes, liquidoLinhaMes, argsProjecaoLinhaMes,
  adiantamentoLinhaMesCentavos, calcValorAdiantamento, rotuloSinalLancamento,
} from "@shared/equipe-fechamento";
import { montarReciboMes, tituloRecibo } from "@shared/equipe-recibo";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { useOrganizacao } from "@/hooks/use-equipe";
import { ReciboDialog } from "../ReciboDialog";
import { LinhaFechamento } from "./LinhaFechamento";
import { AdiantamentoAcoes } from "./AdiantamentoAcoes";
import { LancamentoSheet } from "./LancamentoSheet";
import { chipRegime, ESTADO_LINHA, sublinhaAdiantamento } from "./LinhaMensalistaMes";
import { msgDoErro } from "./msg-do-erro";

/** Todos os dias 'YYYY-MM-DD' entre inicio e fim (inclusive). Sem fuso: só aritmética UTC. */
function diasDoIntervalo(inicio: string, fim: string): string[] {
  const dias: string[] = [];
  const [a, m, d] = inicio.split("-").map(Number);
  let cur = Date.UTC(a, m - 1, d);
  const [fa, fm, fd] = fim.split("-").map(Number);
  const ultimo = Date.UTC(fa, fm - 1, fd);
  while (cur <= ultimo) {
    dias.push(new Date(cur).toISOString().slice(0, 10));
    cur += 86_400_000;
  }
  return dias;
}

const DOW = ["D", "S", "T", "Q", "Q", "S", "S"];

/**
 * O extrato de UMA pessoa na competência (Financeiro v3, §4.5). Desktop:
 * `Sheet` lateral; celular: de baixo.
 *
 * ⚠️ A grade 01–31 NUNCA mostra R$ — só ✓ / falta / –. Mensalista não tem
 * valor de dia; um "R$ 0" ali é lido como falta.
 */
export function ExtratoMensalistaSheet({
  open, onOpenChange, linha, competencia, inicio, fim, dataPagamento, quinzena, isAdmin, pendente,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  linha: LinhaMensalistaMes | null;
  competencia: string;
  inicio: string;
  fim: string;
  dataPagamento: string;
  quinzena: QuinzenaDaCompetencia | null | undefined;
  isAdmin: boolean;
  pendente: boolean;
}) {
  const isMobile = useIsMobile();
  const excluir = useExcluirAdicional();
  const { toast } = useToast();
  const { data: organizacao } = useOrganizacao();
  const [lancando, setLancando] = useState(false);
  const [recibo, setRecibo] = useState(false);

  if (!linha) return null;
  const l = linha;
  const estado = estadoLinhaMes(l);
  const situacao = situacaoAdiantamentoMes(l);
  const chip = chipRegime(l);
  const registro = normalizarDatasISO(l.datas_registro);
  const falta = normalizarDatasISO(l.datas_falta);
  const adiantado = adiantamentoLinhaMesCentavos(l);
  const projecao = argsProjecaoLinhaMes(l);
  const adicionais = l.lancamentos.filter(x => x.sinal !== "DESCONTO");
  const descontos = l.lancamentos.filter(x => x.sinal === "DESCONTO");

  // `LinhaFechamento` fala a língua de `useFechamentos` (camelCase) — monta a
  // partir do snapshot da linha, sem segunda query.
  const fechamentoDoMes: FechamentoDoMes | null = l.fechamento ? {
    id: l.fechamento.id,
    funcionarioId: l.id,
    competencia,
    nome: l.nome,
    apelido: l.apelido,
    salarioFixo: l.fechamento.salario_fixo,
    formaPagamento: l.forma_pagamento,
    adiantamentosPagos: l.fechamento.adiantamentos_pagos,
    variavelApurada: l.fechamento.variavel_apurada,
    variavelObs: l.fechamento.variavel_obs,
    variavelMaxMensal: l.variavel_max_mensal,
    diasRegistro: l.fechamento.dias_registro,
    diasFalta: l.fechamento.dias_falta,
    descontos: l.fechamento.descontos,
    descontosObs: l.fechamento.descontos_obs,
    liquidoFinal: l.fechamento.liquido_final,
    status: l.fechamento.status,
  } : null;

  const pendencia = situacao === "a_gerar" ? {
    funcionarioId: l.id, nome: l.nome, apelido: l.apelido,
    salarioMensal: l.salario_mensal ?? "0", adiantamentoPct: l.adiantamento_pct ?? "0",
    valorSugerido: calcValorAdiantamento(l.salario_mensal, l.adiantamento_pct),
  } : null;

  const onExcluir = async (id: string) => {
    try {
      await excluir.mutateAsync(id);
      toast({ title: "Lançamento excluído." });
    } catch (e) {
      toast({ title: msgDoErro(e, "Não foi possível excluir"), variant: "destructive" });
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          className={isMobile ? "rounded-t-2xl max-h-[92vh] overflow-y-auto pb-8" : "overflow-y-auto sm:max-w-md"}
        >
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2 flex-wrap">
              <Link href={`/equipe/ficha/${l.id}?competencia=${competencia}`} state={origemAtual()} className="hover:underline">
                {l.apelido ?? l.nome}
              </Link>
              <StatusBadge tom={chip.tom} className="text-[10px] font-normal">{chip.texto}</StatusBadge>
              <StatusBadge tom={ESTADO_LINHA[estado].tom} ponto>{ESTADO_LINHA[estado].texto}</StatusBadge>
            </SheetTitle>
            <SheetDescription>
              salário {formatarBRL(salarioLinhaMes(l))}
              {l.fechamento ? " (snapshot do fechamento)" : ""}
              {l.encarregado && l.setores_encarregado.length > 0 && ` · ${l.setores_encarregado.join(", ")}`}
              {" · "}paga em {formatarDataCurta(dataPagamento)}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-4">
            {/* Presença 01–31 — nunca R$. */}
            <section>
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                Presença · {formatarDataCurta(inicio).slice(4)} – {formatarDataCurta(fim).slice(4)}
                {" · "}{l.dias_registro} ✓{l.dias_falta > 0 ? ` · ${l.dias_falta} falta${l.dias_falta === 1 ? "" : "s"}` : ""}
              </h4>
              <div className="grid grid-cols-7 gap-1">
                {diasDoIntervalo(inicio, fim).map(d => {
                  const temRegistro = registro.has(d);
                  const temFalta = falta.has(d);
                  const [a, m, dia] = d.split("-").map(Number);
                  const dow = new Date(Date.UTC(a, m - 1, dia)).getUTCDay();
                  return (
                    <div
                      key={d}
                      className={`rounded-md border py-1 text-center text-[10px] leading-tight ${
                        temFalta ? "bg-atencao-suave border-atencao/30 text-atencao"
                          : temRegistro ? "bg-sucesso-suave border-sucesso/30 text-sucesso"
                          : "bg-muted border-border text-muted-foreground/60"
                      }`}
                      title={d}
                    >
                      <span className="block text-[9px] opacity-70">{DOW[dow]}</span>
                      <span className="block font-medium">{String(dia).padStart(2, "0")}</span>
                      {/* Nunca R$ aqui. */}
                      <span className="block">{temFalta ? "falta" : temRegistro ? "✓" : "–"}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Presença vem das tarefas do dia — nada aqui cria diária.</p>
            </section>

            {/* Adiantamento */}
            <section className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Adiantamento</h4>
              {situacao === "a_gerar" && pendencia ? (
                <div className="rounded-lg border-2 border-erro/30 bg-erro-suave p-2.5 space-y-2">
                  <p className="text-xs text-erro font-semibold">
                    Sem adiantamento em {competencia} — o fechamento pagaria o salário cheio.
                    {" "}Esperado: {formatarBRL(pendencia.valorSugerido)} ({l.adiantamento_pct}%).
                  </p>
                  {isAdmin
                    ? <AdiantamentoAcoes pendencia={pendencia} competencia={competencia} quinzena={quinzena} />
                    : <p className="text-[11px] text-erro">Só um administrador pode gerar, registrar ou dispensar.</p>}
                </div>
              ) : (
                <div className="flex items-center justify-between text-sm">
                  <span className={situacao === "mensal" ? "text-muted-foreground" : ""}>
                    {sublinhaAdiantamento(l, situacao, quinzena) || "—"}
                    {l.adiantamento?.observacao && <span className="block text-[11px] text-muted-foreground">{l.adiantamento.observacao}</span>}
                  </span>
                  <span className="font-semibold tabular-nums">{adiantado > 0 ? `− ${formatarBRL(paraReais(adiantado))}` : "—"}</span>
                </div>
              )}
            </section>

            {/* Extrato do mês */}
            <section className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Fechamento de {competencia}{estado === "projecao" ? " · projeção" : ""}
              </h4>
              {fechamentoDoMes ? (
                <LinhaFechamento
                  f={fechamentoDoMes}
                  adicionalCentavos={l.adicionais_centavos - l.auxilios_centavos}
                  bloqueado={pendente}
                  embutido
                />
              ) : (
                <div className="rounded-lg border bg-card p-3 text-sm space-y-1">
                  <Linha rotulo="fixo" valor={formatarBRL(projecao.salarioFixo)} />
                  <Linha rotulo="− adiantamento" sub={sublinhaAdiantamento(l, situacao, quinzena)} valor={`− ${formatarBRL(projecao.adiantamentosPagos)}`} tom={situacao === "a_gerar" ? "text-erro" : undefined} />
                  <Linha rotulo="+ variável" sub="digitada no fechamento" valor="+ R$ 0,00" />
                  <Linha rotulo="− descontos" sub={descontos.length ? `${descontos.length} lançamento${descontos.length === 1 ? "" : "s"}` : undefined} valor={`− ${formatarBRL(projecao.descontos)}`} />
                  <div className="border-t pt-2 mt-1 flex items-center justify-between font-bold">
                    <span>Líquido (projeção)</span><span className="tabular-nums">{formatarBRL(liquidoLinhaMes(l))}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Mês em andamento — muda com a presença, os lançamentos e as quinzenas. O valor que vale é o do fechamento gerado.</p>
                </div>
              )}
            </section>

            {/* O que sai na QUINZENA (informativo) */}
            <section className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pago na quinzena (informativo)</h4>
              {adicionais.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum adicional no mês.</p>
              ) : adicionais.map(x => (
                <LinhaLancamento key={x.id} x={x} onExcluir={onExcluir} excluindo={excluir.isPending} />
              ))}
              {descontos.length > 0 && (
                <>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground pt-1">Descontos do mês (abatem no fechamento)</h4>
                  {descontos.map(x => (
                    <LinhaLancamento key={x.id} x={x} onExcluir={onExcluir} excluindo={excluir.isPending} />
                  ))}
                </>
              )}
              {estado !== "fechado" && (
                <Button variant="outline" className="w-full min-h-11 border-dashed" onClick={() => setLancando(true)} data-testid="btn-novo-lancamento-mes">
                  <Plus className="h-4 w-4 mr-1" />Lançamento (adicional ou desconto)
                </Button>
              )}
            </section>

            {/* O recibo do mês, para mandar para a pessoa. Enquanto o mês não
                foi fechado o documento se chama DEMONSTRATIVO e diz que é
                projeção — ver `montarReciboMes`. */}
            <section>
              <Button
                variant="outline"
                className="w-full min-h-11"
                onClick={() => setRecibo(true)}
                data-testid="btn-recibo-mes"
              >
                <Share2 className="h-4 w-4 mr-1" />
                {estado === "fechado" ? "Compartilhar recibo do mês" : "Compartilhar demonstrativo do mês"}
              </Button>
            </section>
          </div>
        </SheetContent>
      </Sheet>

      <ReciboDialog
        open={recibo}
        onClose={() => setRecibo(false)}
        titulo={tituloRecibo({ pessoa: l, periodo: competencia, pago: estado === "fechado" })}
        aviso={estado === "fechado"
          ? undefined
          : "O mês ainda não foi fechado — o texto sai como demonstrativo, com os valores projetados."}
        texto={montarReciboMes({
          organizacao: organizacao?.nome ?? "Fazenda",
          competencia,
          dataPagamento,
          linha: l,
          geradoEm: hojeNaFazenda(),
        })}
      />

      <LancamentoSheet
        open={lancando}
        onOpenChange={setLancando}
        funcionario={{ id: l.id, nome: l.nome, apelido: l.apelido, tipo_remuneracao: l.tipo_remuneracao }}
        competencia={competencia}
        inicio={inicio}
        fim={fim}
      />
    </>
  );
}

function Linha({ rotulo, sub, valor, tom }: { rotulo: string; sub?: string; valor: string; tom?: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">
        {rotulo}{sub && <span className={`block text-[11px] ${tom ?? ""}`}>{sub}</span>}
      </span>
      <span className={`font-medium tabular-nums ${tom ?? ""}`}>{valor}</span>
    </div>
  );
}

function LinhaLancamento({ x, onExcluir, excluindo }: {
  x: LinhaMensalistaMes["lancamentos"][number];
  onExcluir: (id: string) => void;
  excluindo: boolean;
}) {
  const desconto = x.sinal === "DESCONTO";
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="min-w-0">
        <span className={`font-medium ${desconto ? "text-erro" : "text-sucesso"}`}>{rotuloSinalLancamento(x.sinal)}</span>
        <span className="block text-[11px] text-muted-foreground truncate">
          {formatarDataCurta(x.data)}{x.descricao ? ` · ${x.descricao}` : ""}{x.tipo === "AUXILIO" ? " · auxílio" : ""}
        </span>
      </span>
      <span className={`font-semibold tabular-nums shrink-0 ${desconto ? "text-erro" : "text-sucesso"}`}>
        {desconto ? "− " : "+ "}{formatarBRL(x.valor)}
      </span>
      <button
        type="button"
        className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-erro disabled:opacity-40"
        disabled={excluindo}
        onClick={() => onExcluir(x.id)}
        aria-label="Excluir lançamento"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
